import UploadIcon from '@mui/icons-material/Upload';
import { Box, styled } from '@mui/material';
import { FC } from 'react';
import {
  BooleanField,
  BooleanInput,
  Link,
  NumberField,
  NumberInput,
  SearchInput,
  SelectArrayInput,
  SelectInput,
  TextField,
  TextInput,
  TopToolbar,
  useGetIdentity,
} from 'react-admin';

import { GetB2bEmployeesQuery } from '@Common/types/bo/b2b/employees';
import {
  EmployeeListExternalRow,
  getBoExternalEmployeesSortColumns,
} from '@Common/types/bo_external/employees';

import { planCategoryChoices } from '@sh4res/bo-common/choices/plans';
import { DatagridTyped, FilterButton, List } from '@sh4res/bo-common/components';
import { B2bEmployeeCreate } from '@sh4res/bo-common/components/B2bEmployee/B2bEmployeeCreate';
import { BulkSendInvitesButton } from '@sh4res/bo-common/components/B2bEmployee/BulkSendInvitesButton';
import {
  accountStatusChoices,
  genderChoices,
} from '@sh4res/bo-common/components/B2bEmployee/choices';
import { EmployeesBulkExportButton } from '@sh4res/bo-common/components/B2bEmployee/EmployeesBulkExportButton';
import { B2bEmployeeBulkCreate } from '@sh4res/bo-common/components/B2bEmployeeBulkCreate/B2bEmployeeBulkCreate';
import {
  B2bContractArraySelectInput,
  ContractsField,
  IdField,
  ProductsField,
  SelectChipField,
  UTCDateField,
} from '@sh4res/bo-common/fields';
import { DateInput } from '@sh4res/bo-common/fields/DateInput';
import { useSelectedColumns } from '@sh4res/bo-common/hooks';
import { ColumnList, RaSort } from '@sh4res/bo-common/utils';
import {
  CountryCodeChoices,
  useCountryCodeChoicesByName,
} from '@sh4res/bo-common/utils/countryChoices';
import { createFilters } from '@sh4res/bo-common/utils/create_filters';

import { kybStatusChoices } from '@Resources/companies/choices';
import { socialStatusChoices, statusChoices } from '@Resources/employees/choices';

const createB2bEmployeesFilters = (countryCodeChoicesByName: CountryCodeChoices) => {
  const testId = 'BoExternalEmployeesList';
  return createFilters<GetB2bEmployeesQuery>({
    id: <NumberInput source="" data-testid={`${testId}_IdFilter`} />,
    query: <SearchInput source="" data-testid={`${testId}_QueryFilter`} />,
    company_id: <NumberInput source="" data-testid={`${testId}_CompanyIdFilter`} />,
    campaign_id: <NumberInput source="" data-testid={`${testId}_CampaignIdFilter`} />,
    user_id: <NumberInput source="" data-testid={`${testId}_UserIdFilter`} />,
    firstname: <TextInput source="" data-testid={`${testId}_FirstnameFilter`} />,
    lastname: <TextInput source="" data-testid={`${testId}_LastnameFilter`} />,
    email: <TextInput source="" data-testid={`${testId}_EmailFilter`} />,
    phone_number: <TextInput source="" data-testid={`${testId}_PhoneNumberFilter`} />,
    street: <TextInput source="" data-testid={`${testId}_StreetFilter`} />,
    postalcode: <TextInput source="" data-testid={`${testId}_PostalCodeFilter`} />,
    city: <TextInput source="" data-testid={`${testId}_CityFilter`} />,
    country: (
      <SelectArrayInput
        choices={countryCodeChoicesByName}
        data-testid={`${testId}_CountryFilter`}
      />
    ),
    language: <TextInput source="" data-testid={`${testId}_LanguageFilter`} />,
    is_activated_by_company: (
      <BooleanInput source="" data-testid={`${testId}_IsActivatedByCompanyFilter`} />
    ),
    activation_code_is_used: (
      <BooleanInput source="" data-testid={`${testId}_ActivationCodeIsUsedFilter`} />
    ),
    status: <SelectInput choices={statusChoices} data-testid={`${testId}_StatusFilter`} />,
    from_created_at: <DateInput source="" data-testid={`${testId}_FromCreatedAtFilter`} />,
    to_created_at: <DateInput source="" data-testid={`${testId}_ToCreatedAtFilter`} />,
    company_name: <TextInput source="" data-testid={`${testId}_CompanyNameFilter`} />,
    account_status: (
      <SelectArrayInput
        choices={accountStatusChoices}
        data-testid={`${testId}_AccountStatusFilter`}
      />
    ),
    approbation_state: <TextInput source="" data-testid={`${testId}_ApprobationStateFilter`} />,
    nir: <TextInput source="" data-testid={`${testId}_NirFilter`} />,
    ntt: <TextInput source="" data-testid={`${testId}_NttFilter`} />,
    date_of_birth: <DateInput source="" data-testid={`${testId}_DateOfBirthFilter`} />,
    csp_code: <TextInput source="" data-testid={`${testId}_CspCodeFilter`} />,
    contract_ids: (
      <B2bContractArraySelectInput
        source=""
        reference="contracts"
        filter={{}}
        testId={`${testId}_ContractIdsFilter`}
      />
    ),
    products: (
      <SelectArrayInput choices={planCategoryChoices} data-testid={`${testId}_ProductsFilter`} />
    ),
    is_user_closed_or_archived: (
      <BooleanInput source="" data-testid={`${testId}_IsUserClosedOrArchivedFilter`} />
    ),
    registration_number: <TextInput source="" data-testid={`${testId}_RegistrationNumberFilter`} />,
  });
};

const defaultSort: RaSort<GetB2bEmployeesQuery> = { field: 'id', order: 'DESC' };

const b2bEmployeesListColumns: ColumnList<EmployeeListExternalRow> = {
  id: (
    <IdField source="id" reference="employees" data-testid={'BoExternalEmployeesList_IdColumn'} />
  ),
  company_id: <NumberField data-testid={'BoExternalEmployeesList_CompanyIdColumn'} />,
  company_name: <TextField data-testid={'BoExternalEmployeesList_CompanyNameColumn'} />,
  user_id: <NumberField data-testid={'BoExternalEmployeesList_UserIdColumn'} />,
  firstname: <TextField data-testid={'BoExternalEmployeesList_FirstnameColumn'} />,
  lastname: <TextField data-testid={'BoExternalEmployeesList_LastnameColumn'} />,
  email: <TextField data-testid={'BoExternalEmployeesList_EmailColumn'} />,
  phone_number: <TextField data-testid={'BoExternalEmployeesList_PhoneNumberColumn'} />,
  street: <TextField data-testid={'BoExternalEmployeesList_StreetColumn'} />,
  postalcode: <TextField data-testid={'BoExternalEmployeesList_PostalCodeColumn'} />,
  city: <TextField data-testid={'BoExternalEmployeesList_CityColumn'} />,
  country: <TextField data-testid={'BoExternalEmployeesList_CountryColumn'} />,
  language: <TextField data-testid={'BoExternalEmployeesList_LanguageColumn'} />,
  is_activated_by_company: (
    <BooleanField data-testid={'BoExternalEmployeesList_IsActivatedByCompanyColumn'} />
  ),
  activation_code_is_used: (
    <BooleanField data-testid={'BoExternalEmployeesList_ActivationCodeIsUsedColumn'} />
  ),
  personal_details_confirmed: (
    <BooleanField data-testid={'BoExternalEmployeesList_PersonalDetailsConfirmedColumn'} />
  ),
  last_invitation_sent_at: (
    <UTCDateField showTime data-testid={'BoExternalEmployeesList_LastInvitationSentAtColumn'} />
  ),
  status: (
    <SelectChipField
      source=""
      choices={statusChoices}
      data-testid={'BoExternalEmployeesList_StatusColumn'}
    />
  ),
  created_at: <UTCDateField data-testid={'BoExternalEmployeesList_CreatedAtColumn'} />,
  updated_at: <UTCDateField data-testid={'BoExternalEmployeesList_UpdatedAtColumn'} />,
  date_of_birth: <UTCDateField data-testid={'BoExternalEmployeesList_DateOfBirthColumn'} />,
  place_of_birth: <TextField data-testid={'BoExternalEmployeesList_PlaceOfBirthColumn'} />,
  postcode_of_birth: <TextField data-testid={'BoExternalEmployeesList_PostcodeOfBirthColumn'} />,
  nationality: <TextField data-testid={'BoExternalEmployeesList_NationalityColumn'} />,
  tax_number: <TextField data-testid={'BoExternalEmployeesList_TaxNumberColumn'} />,
  investor_profile: <TextField data-testid={'BoExternalEmployeesList_InvestorProfileColumn'} />,
  pei_investor_profile: (
    <TextField data-testid={'BoExternalEmployeesList_PeiInvestorProfileColumn'} />
  ),
  work_starting_date: (
    <UTCDateField data-testid={'BoExternalEmployeesList_WorkStartingDateColumn'} />
  ),
  work_ending_date: <UTCDateField data-testid={'BoExternalEmployeesList_WorkEndingDateColumn'} />,
  leaving_reason: <TextField data-testid={'BoExternalEmployeesList_LeavingReasonColumn'} />,
  tax_residence_code: <TextField data-testid={'BoExternalEmployeesList_TaxResidenceCodeColumn'} />,
  kyb_status: (
    <SelectInput
      choices={kybStatusChoices}
      data-testid={'BoExternalEmployeesList_KybStatusColumn'}
    />
  ),
  net_employee_deposits: (
    <NumberField data-testid={'BoExternalEmployeesList_NetEmployeeDepositsColumn'} />
  ),
  net_employer_contributions: (
    <NumberField data-testid={'BoExternalEmployeesList_NetEmployerContributionsColumn'} />
  ),
  retirement_date: <UTCDateField data-testid={'BoExternalEmployeesList_RetirementDateColumn'} />,
  social_status: (
    <SelectChipField
      source="social_status"
      choices={socialStatusChoices}
      data-testid={'BoExternalEmployeesList_SocialStatusColumn'}
    />
  ),
  gender: (
    <SelectChipField
      source="gender"
      choices={genderChoices}
      data-testid={'BoExternalEmployeesList_GenderColumn'}
    />
  ),
  nir: <TextField data-testid={'BoExternalEmployeesList_NirColumn'} />,
  ntt: <TextField data-testid={'BoExternalEmployeesList_NttColumn'} />,
  name_in_use: <TextField data-testid={'BoExternalEmployeesList_NameInUseColumn'} />,
  registration_number: (
    <TextField data-testid={'BoExternalEmployeesList_RegistrationNumberColumn'} />
  ),
  role_in_company: <TextField data-testid={'BoExternalEmployeesList_RoleInCompanyColumn'} />,
  country_of_birth: <TextField data-testid={'BoExternalEmployeesList_CountryOfBirthColumn'} />,
  account_status: (
    <SelectChipField
      source="account_status"
      choices={accountStatusChoices}
      data-testid={'BoExternalEmployeesList_AccountStatusColumn'}
    />
  ),
  approbation_state: <TextField data-testid={'BoExternalEmployeesList_ApprobationStateColumn'} />,
  is_phone_number_user_controlled: (
    <BooleanField data-testid={'BoExternalEmployeesList_IsPhoneNumberUserControlledColumn'} />
  ),
  is_email_user_controlled: (
    <BooleanField data-testid={'BoExternalEmployeesList_IsEmailUserControlledColumn'} />
  ),
  is_mailing_address_user_controlled: (
    <BooleanField data-testid={'BoExternalEmployeesList_IsMailingAddressUserControlledColumn'} />
  ),
  is_address_user_controlled: (
    <BooleanField data-testid={'BoExternalEmployeesList_IsAddressUserControlledColumn'} />
  ),
  is_retirement_age_user_controlled: (
    <BooleanField data-testid={'BoExternalEmployeesList_IsRetirementAgeUserControlledColumn'} />
  ),
  csp_code: <TextField data-testid={'BoExternalEmployeesList_CspCodeColumn'} />,
  contract_ids: (
    <ContractsField
      source="contract_ids"
      data-testid={'BoExternalEmployeesList_ContractIdsColumn'}
    />
  ),
  products: <ProductsField data-testid={'BoExternalEmployeesList_ProductsColumn'} />,
  is_campaigns_blocked: (
    <BooleanField data-testid={'BoExternalEmployeesList_IsCampaignsBlockedColumn'} />
  ),
  is_campaigns_blocked_regulatory: (
    <BooleanField data-testid={'BoExternalEmployeesList_IsCampaignsBlockedRegulatoryColumn'} />
  ),
  is_user_closed_or_archived: (
    <BooleanField data-testid={'BoExternalEmployeesList_IsUserClosedOrArchivedColumn'} />
  ),
  id_external_taiga: <NumberField data-testid={'BoExternalEmployeesList_IdExternalTaigaColumn'} />,
};

const defaultColumns: (keyof EmployeeListExternalRow)[] = [
  'id',
  'firstname',
  'lastname',
  'email',
  'status',
  'account_status',
  'contract_ids',
  'created_at',
];

const B2bEmployeesActionsItem = styled(Box)`
  margin: ${({ theme }) => theme.spacing(0, 0, 0, 0.5)};
`;

const B2bEmployeesActions = ({ companyId }: { companyId: number }) => (
  <TopToolbar>
    <B2bEmployeesActionsItem>
      <nav aria-label="list actions">
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            alignItems: 'center',
          }}>
          <li style={{ margin: 0, padding: 0 }}>
            <FilterButton data-testid={'BoExternalB2bEmployeesActions_FilterButton'} />
          </li>
          <li style={{ margin: 0, padding: 0 }}>
            <B2bEmployeeCreate
              data-tour-id="employee-create-action-btn"
              allowCompanyId={false}
              resource="employees"
            />
          </li>
          <li style={{ margin: 0, padding: 0 }}>
            <B2bEmployeeBulkCreate
              data-tour-id="employee-bulk-upload-action-btn"
              LinkComponent={Link}
              label="resources.bo_common.b2b.employees.actions.bulk_create.label"
              variant="text"
              sx={{ height: '27px' }}
              icon={<UploadIcon />}
              companyId={companyId}
            />
          </li>
          <li style={{ margin: 0, padding: 0 }}>
            <EmployeesBulkExportButton
              kind="employees_bulk"
              data-testid={'BoExternalB2bEmployeesActions_ExportButton'}
            />
          </li>
        </ul>
      </nav>
    </B2bEmployeesActionsItem>
  </TopToolbar>
);

export const EmployeesList: FC = () => {
  const countryCodeChoicesByName = useCountryCodeChoicesByName();
  const b2bEmployeesFilters = createB2bEmployeesFilters(countryCodeChoicesByName);

  const displayedColumns = useSelectedColumns<EmployeeListExternalRow>({
    preferences: 'employees.list.columns',
    defaultColumns,
  });

  const { data: identity, isLoading } = useGetIdentity();

  if (isLoading || !identity) {
    return null;
  }

  return (
    <List
      actions={<B2bEmployeesActions companyId={identity.companyId[0]} />}
      sort={defaultSort}
      filters={b2bEmployeesFilters}
      empty={false}>
      <DatagridTyped<EmployeeListExternalRow>
        rowClick="show"
        isRowSelectable={(record) =>
          !(record?.is_activated_by_company && record?.activation_code_is_used) &&
          !record?.is_user_closed_or_archived &&
          record?.kyb_status === 'verified'
        }
        bulkActionButtons={
          <BulkSendInvitesButton testId="BoExternalEmployeesList_BulkSendInvitesButton" />
        }
        columnRegistry={b2bEmployeesListColumns}
        displayedColumns={displayedColumns}
        sortableColumns={getBoExternalEmployeesSortColumns}
      />
    </List>
  );
};
